import React, { useState, useEffect, useCallback } from 'react';
import { Table, Form, Input, Tag, Button, Checkbox, message, Space, Select, Popconfirm } from 'antd';
import DraggableModal from '../../components/DraggableModal';
import { PlusOutlined } from '@ant-design/icons';
import api from '../../api';
import { getStoredUser, isSuperAdmin } from '../../userAuth';

export default function Permissions() {
  const user = getStoredUser();
  const [categories, setCategories] = useState([]);
  const [permDefs, setPermDefs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm] = Form.useForm();
  const [addForm] = Form.useForm();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/permission-categories');
      setCategories(Array.isArray(res?.items) ? res.items : []);
      setPermDefs(Array.isArray(res?.permissions) ? res.permissions : []);
    } catch {
      message.error('获取数据失败');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!isSuperAdmin(user)) return <div style={{ padding: 40, textAlign: 'center', fontSize: 18 }}>无权限</div>;

  const handleEdit = (record) => {
    setEditing(record);
    editForm.setFieldsValue({
      name: record.name,
      perm_keys: record.perm_keys || [],
      status: record.status ?? 1,
    });
    setEditModal(true);
  };

  const handleEditSave = async () => {
    try {
      const values = await editForm.validateFields();
      await api.put(`/permission-categories/${editing.id}`, values);
      message.success('更新成功');
      setEditModal(false);
      fetchData();
    } catch { /* validation or api error */ }
  };

  const handleAddSave = async () => {
    try {
      const values = await addForm.validateFields();
      await api.post('/permission-categories', values);
      message.success('新增角色类别成功');
      setAddModal(false);
      addForm.resetFields();
      fetchData();
    } catch { /* validation or api error */ }
  };

  const handleDelete = async (record) => {
    try {
      await api.delete(`/permission-categories/${record.id}`);
      message.success('删除成功');
      fetchData();
    } catch (err) {
      message.error(err?.message || '删除失败');
    }
  };

  const columns = [
    { title: '角色类别', dataIndex: 'name', key: 'name' },
    {
      title: '功能权限',
      dataIndex: 'perm_keys',
      key: 'perm_keys',
      render: (keys) => (
        <Space wrap>
          {(keys || []).map(k => {
            const def = permDefs.find(p => p.key === k);
            return <Tag color="blue" key={k}>{def?.label || k}</Tag>;
          })}
        </Space>
      ),
    },
    { title: '关联用户数', dataIndex: 'user_count', key: 'user_count' },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (s) => <Tag color={s === 1 ? 'green' : 'red'}>{s === 1 ? '启用' : '禁用'}</Tag>,
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size={4}>
          <Button type="link" style={{ padding: 0 }} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title={`确认删除角色类别「${record.name}」？`}
            onConfirm={() => handleDelete(record)}
          >
            <Button type="link" danger style={{ padding: 0 }}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>角色类别管理</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { addForm.resetFields(); setAddModal(true); }}>
          新增角色类别
        </Button>
      </div>

      <div style={{ background: '#fff', borderRadius: 8, padding: 24, marginBottom: 24 }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>角色类别列表</h3>
        <p style={{ color: '#999', margin: '4px 0 16px' }}>可按角色类别勾选功能权限，区域设置时可绑定负责人与角色类别</p>
        <Table
          columns={columns}
          dataSource={categories}
          rowKey="id"
          loading={loading}
          pagination={false}
        />
      </div>

      {/* Edit Modal */}
      <DraggableModal
        title="编辑角色类别"
        open={editModal}
        onOk={handleEditSave}
        onCancel={() => setEditModal(false)}
        okText="保存"
        cancelText="取消"
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="name" label="类别名称" rules={[{ required: true, message: '请输入类别名称' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="perm_keys" label="功能权限">
            <Checkbox.Group options={permDefs.map(p => ({ value: p.key, label: p.label }))} />
          </Form.Item>
          <Form.Item name="status" label="状态" rules={[{ required: true, message: '请选择状态' }]}>
            <Select options={[{ value: 1, label: '启用' }, { value: 0, label: '禁用' }]} />
          </Form.Item>
        </Form>
      </DraggableModal>

      {/* Add Modal */}
      <DraggableModal
        title="新增角色类别"
        open={addModal}
        onOk={handleAddSave}
        onCancel={() => setAddModal(false)}
        okText="确定"
        cancelText="取消"
      >
        <Form form={addForm} layout="vertical">
          <Form.Item name="name" label="类别名称" rules={[{ required: true, message: '请输入类别名称' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="perm_keys" label="功能权限">
            <Checkbox.Group options={permDefs.map(p => ({ value: p.key, label: p.label }))} />
          </Form.Item>
        </Form>
      </DraggableModal>
    </div>
  );
}
